import {
  BoosterDtoTypeEnum,
  CampaignConfigDto,
  CampaignsApi,
  CampaignsControllerListCommunicationChannelsEnum,
  CashbackCampaignUsersApi,
  DiscountDto,
  DiscountsApi,
  PopulatedCampaignUserDto,
  PopulatedCampaignUserDtoStatusEnum,
  PopulatedCampaignUserRefereeDto,
  ProductDto,
  ProductsApi,
  RewardConfigDtoTypeEnum,
} from '@coretava/api/ecommerce-service';
import { AppSurveyDto, SurveysApi } from '@coretava/api/events-service';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { delay } from '@gamiphy/utils/general';
import compact from 'lodash/compact';
import {
  action, computed, makeAutoObservable, observable, observe, runInAction, when,
} from 'mobx';

class CampaignUserStore {
  @observable initialized = false;

  @observable campaigns?: CampaignConfigDto[] = undefined;

  @observable campaignUsers?: PopulatedCampaignUserDto[] = undefined;

  @observable refereeCampaignUser?: PopulatedCampaignUserRefereeDto | null = null;

  @observable campaignsDiscounts?: DiscountDto[] = undefined;

  @observable bogoCampaignBuyProducts?: ProductDto[] = undefined;

  @observable bogoCampaignGetProduct?: ProductDto = undefined;

  @observable refereeCampaignDiscount?: DiscountDto = undefined;

  @observable appSurvey?: AppSurveyDto = undefined;

  constructor() {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed
  get campaignUsersApi(): CashbackCampaignUsersApi {
    return new CashbackCampaignUsersApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get campaignsApi(): CampaignsApi {
    return new CampaignsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get surveysApi(): SurveysApi {
    return new SurveysApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get discountsApi(): DiscountsApi {
    return new DiscountsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get productApi(): ProductsApi {
    return new ProductsApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @computed
  get surveyCampaignUser(): PopulatedCampaignUserDto | undefined {
    return this.campaignUsers?.find((c) => c.campaign.booster?.type === BoosterDtoTypeEnum.Survey);
  }

  @computed
  get referralCampaignUser(): PopulatedCampaignUserDto | undefined {
    return this.campaignUsers?.find((c) => c.campaign.booster?.type === BoosterDtoTypeEnum.Referal);
  }

  @computed
  get basicCampaignUser(): PopulatedCampaignUserDto | undefined {
    return this.campaignUsers?.find((c) => c.campaign.booster?.type === BoosterDtoTypeEnum.Basic);
  }

  @computed
  get spinTheWheelCampaignUser(): PopulatedCampaignUserDto | undefined {
    return this.campaignUsers?.find((c) => c.campaign.booster?.type === BoosterDtoTypeEnum.Wheel);
  }

  @computed
  get spinTheWheelCampaign(): CampaignConfigDto | undefined {
    return this.campaigns?.find((campaign) => campaign.booster?.type === BoosterDtoTypeEnum.Wheel);
  }

  @computed
  get bogoCampaignUser(): PopulatedCampaignUserDto | undefined {
    return this.campaignUsers?.find((c) => c.campaign.rewardConfig?.type === RewardConfigDtoTypeEnum.Bogo);
  }

 @computed
  public get getCampaignDiscount(): (campaignUser: PopulatedCampaignUserDto) => DiscountDto | undefined {
    return (campaignUser) => this.campaignsDiscounts?.find((discount) => discount.id === campaignUser.discount?.id);
  }

 @action
 async fetchCampaigns() {
   await when(() => AppStore.initialized);

   const res = await this.campaignsApi.campaignsControllerList(
     AppStore.app.id,
     undefined,
     [CampaignsControllerListCommunicationChannelsEnum.Widget],
     undefined,
     undefined,
     undefined,
     undefined,
     undefined,
     true,
   );

   runInAction(() => {
     this.campaigns = res.data.campaigns;
   });
 }

 @action
 async fetchCampaignUsers() {
   await when(() => AppStore.initialized && UserStore.isLoggedIn);

   const res = await this.campaignUsersApi.campaignUsersControllerGetCampaignUsers(AppStore.app.id);

   runInAction(() => {
     this.campaignUsers = res.data.campaignUsers;
   });
 }

 @action
 async fetchRefereeCampaignUsers() {
   await when(() => AppStore.initialized && UserStore.isLoggedIn);

   const refereeCampaignUser = await this.campaignUsersApi.campaignUsersControllerGetActiveCampaignUserReferee(AppStore.app.id);

   runInAction(() => {
     this.refereeCampaignUser = refereeCampaignUser.data.campaignUserReferee;
   });
 }

 @action
 async watchBasicCampaign() {
   await when(() => AppStore.initialized && UserStore.isLoggedIn && !!this.basicCampaignUser);

   const interval = setInterval(async () => {
     if (this.basicCampaignUser?.status === PopulatedCampaignUserDtoStatusEnum.Pending) {
       await this.fetchCampaignUsers();
     } else {
       clearInterval(interval);
     }
   }, 1000);
 }

 @action
 async fetchAppSurvey() {
   await when(() => !!this.surveyCampaignUser);

   if (this.surveyCampaignUser?.campaign?.booster?.id) {
     const response = await this.surveysApi.appSurveysControllerGet(AppStore.app.id, this.surveyCampaignUser.campaign.booster.id);
     runInAction(() => {
       this.appSurvey = response.data.survey;
     });
   }
 }

 @action
 async getBogoProducts(campaign: CampaignConfigDto) {
   if (campaign.rewardConfig?.bogo && campaign.rewardConfig.skuGift?.productId) {
     const [rewardProduct, ...requiredProducts] = await Promise.all([
       this.productApi.productsControllerGetByExternalId(AppStore.app.id, campaign.rewardConfig.skuGift?.productId),
       ...compact(campaign.rewardConfig?.bogo.listSkuBuyTogether.map((prod) => (prod.productId
         ? this.productApi.productsControllerGetByExternalId(AppStore.app.id, prod.productId)
         : undefined))),
     ]);
     runInAction(() => {
       this.bogoCampaignGetProduct = rewardProduct.data.product;
       this.bogoCampaignBuyProducts = requiredProducts.map((res) => res.data.product);
     });
   }
 }

 @action
 async fetchCampaignsDiscounts() {
   await when(() => !!this.campaignUsers);

   if (AppStore.app.id && !!this.campaignUsers) {
     const results = await Promise.all(this.campaignUsers.map((camUser) => (camUser.discount ? this.discountsApi.discountsControllerGet(AppStore.app.id, camUser.discount.id) : undefined)));

     runInAction(() => {
       this.campaignsDiscounts = compact(results)
         .map((discountRes) => discountRes?.data.discount);
     });
   }
 }

 @action
 async fetchRefereeCampaignDiscounts() {
   await when(() => !!this.refereeCampaignUser);

   if (AppStore.app.id && !!this.refereeCampaignUser?.refereeDiscount) {
     const result = await this.discountsApi.discountsControllerGet(AppStore.app.id, this.refereeCampaignUser.refereeDiscount.id);

     runInAction(() => {
       this.refereeCampaignDiscount = result.data.discount;
     });
   }
 }

 @action
 private async initialize(): Promise<void> {
   await when(() => AppStore.initialized && AppStore.app.featureFlags.campaignEnabled);

   observe(this, 'campaignUsers', () => {
     void this.fetchCampaignsDiscounts();
   });

   observe(this, 'refereeCampaignUser', () => {
     void this.fetchRefereeCampaignDiscounts();
   });

   observe(this, 'bogoCampaignUser', () => {
     if (this.bogoCampaignUser) {
       void this.getBogoProducts(this.bogoCampaignUser.campaign);
     }
   });

   observe(this, 'campaigns', () => {
     const bogoCampaign = this.campaigns?.find((c) => c.rewardConfig?.type === RewardConfigDtoTypeEnum.Bogo);
     if (bogoCampaign) {
       void this.getBogoProducts(bogoCampaign);
     }
   });

   void this.watchBasicCampaign();

   await Promise.all([
     this.fetchCampaigns(),
     this.fetchCampaignUsers(),
     this.fetchRefereeCampaignUsers(),
     this.fetchAppSurvey(),
   ]);

   void delay(1000).then(async () => {
     await this.fetchCampaignUsers();
   });

   runInAction(() => {
     this.initialized = true;
   });
 }
}

export default new CampaignUserStore();
